/**
 * Chat, in one place outside App.js (the API's api/chat).
 *
 * Not per organisation: the person's conversations and their missions' tasks,
 * whichever organisation they are in, side by side as Messenger would show them.
 * It owns whether the chat is open and how (a panel, full screen, or folded
 * down to a pill), the two lists (people and groups; missions and tasks), who a
 * new chat can be with, which room is open, and every unread count behind the
 * Chats badge. The counts follow the socket: each conversation frame carries
 * the person's own unread count for it.
 *
 * Screens read it with useChat (a React hook) or getState; everything that
 * changes it goes through its methods.
 */
import { useEffect, useReducer, useRef } from "react"
import { toast } from "react-toastify"
import chatApi from "./chat.api"

export const CHAT_MODE = { PANEL: "panel", FULL: "full" }

/** A frame for a task the column lacks reloads it this much later: a burst is one reload. */
const TASKS_RELOAD_DELAY_MS = 2000

/** After a dropped connection, the resync waits up to this long: not every client at once after a deploy. */
const RESYNC_SPREAD_MS = 15000

/** A frame for the room on screen reads it this much later: a burst of messages is one read. */
const OPEN_ROOM_READ_DELAY_MS = 2000

/** The task column, when chat shows again, is read again at most this often (frames keep it current). */
const TASKS_REFRESH_MS = 60 * 1000

/** A task a reload did not find is not reloaded for again for this long (access answers last a minute). */
const UNLISTED_TASK_MS = 2 * 60 * 1000

const MODE_KEY = "x-chat-mode"
/** The chat or task room opened last: chat opens on it again (a refresh included). */
const LAST_ROOM_KEY = "x-chat-last-room"

const rememberRoom = (room) => {
    try {
        localStorage.setItem(LAST_ROOM_KEY, JSON.stringify(room))
    } catch (err) {
        // Storage full or off: chat just opens without it
    }
}

const lastRoom = () => {
    try {
        return JSON.parse(localStorage.getItem(LAST_ROOM_KEY) || "null")
    } catch (err) {
        return null
    }
}

const forgetRoom = () => localStorage.removeItem(LAST_ROOM_KEY)
const ROOMS_FOLDED_KEY = "x-chat-rooms-folded"

const byLatest = (a, b) => new Date(b.lastMessageAt || b.createdAt || 0) - new Date(a.lastMessageAt || a.createdAt || 0)

const initialState = () => ({
    open: false,
    /** Folded down to a pill: still "open", the thread kept. */
    collapsed: false,
    // Always the panel: full screen is gone (an old saved choice must not leave anyone stuck in it)
    mode: CHAT_MODE.PANEL,
    /** Direct messages and groups, latest first. */
    conversations: [],
    conversationsLoaded: false,
    /** The task column: { missions, tasks } */
    tasks: { missions: [], tasks: [] },
    tasksLoaded: false,
    /** Who a new chat can be with ({_id, firstName, lastName, avatar}). */
    people: [],
    peopleLoaded: false,
    /**
     * The open room: { kind: "conversation", conversationId } | { kind: "task", taskId } | { kind: "new" }
     */
    room: null,
    /** The person or group picked in the first column: the "General" of the second. */
    contextId: null,
    /** Unread per conversation id: { org, kind, actionItem?, unread } */
    unread: {},
    /**
     * The latest message that came in for the person (their unread went up):
     * { conversationId, kind, actionItem?, title, text, byName, at }. The folded
     * pill previews it and opens on it.
     */
    incoming: null,
    /** Missions opened in the task column: every one starts folded, each time. */
    openMissions: {},
    /** The task column folded to a rail. */
    roomsFolded: localStorage.getItem(ROOMS_FOLDED_KEY) === "true",
})

function createChatStore(client = chatApi) {
    let state = initialState()
    let sockets = []
    let generation = 0
    let tasksLoading = null
    const listeners = new Set()
    const incomingListeners = new Set()
    // Each conversation's newest version seen (its updatedAt): frames can arrive
    // out of order (several API instances), an older one never wins.
    const versions = new Map()
    // Tasks a frame named that the column does not list (archived, out of reach):
    // not reloaded for again for a while (task id -> when). And the reload a frame
    // asks for, a moment later.
    const unlistedTasks = new Map()
    const tasksToFind = new Set()
    let tasksReloadTimer = null
    // Conversations a frame named without members that the list lacked: reloaded once
    const listReloadedFor = new Set()
    // Each task's conversation, from the views seen (openTask's answer, frames):
    // a task room not in the column still reads the light way
    const taskConversations = new Map()
    let openRoomReadTimer = null
    let resyncTimer = null
    let tasksLoadedAt = 0

    const getState = () => state

    const set = (patch) => {
        state = { ...state, ...patch }
        listeners.forEach((fn) => fn(state))
    }

    const subscribe = (fn) => {
        listeners.add(fn)
        return () => listeners.delete(fn)
    }

    /** Total unread for the badge. */
    const totalUnread = () => Object.values(state.unread).reduce((sum, row) => sum + (row.unread || 0), 0)

    const withUnread = (unread, view) => {
        const next = { ...unread }

        if (view.unread > 0) {
            next[view._id] = { org: view.org, kind: view.kind, actionItem: view.actionItem, unread: view.unread }
        } else {
            delete next[view._id]
        }

        return next
    }

    /** What a conversation is called in the pill's preview. */
    const titleOf = (view) => {
        if (view.kind === "task") {
            return state.tasks.tasks.find((t) => t._id === view.actionItem)?.title || "A task"
        }

        if (view.title) return view.title

        return view.lastMessage?.byName || ""
    }

    /** A conversation as the person now sees it: in its list, its task row, the badge. */
    const applyView = (view) => {
        if (!view?._id) return

        const version = view.updatedAt ? Date.parse(view.updatedAt) : NaN

        if (!Number.isNaN(version)) {
            if (versions.get(view._id) > version) return
            versions.set(view._id, version)
        }

        const patch = { unread: withUnread(state.unread, view) }
        const before = state.unread[view._id]?.unread || 0
        // Their unread went up: somebody wrote to them
        const arrived = view.unread > before && view.lastMessage?.text

        if (arrived) {
            patch.incoming = {
                conversationId: view._id,
                kind: view.kind,
                actionItem: view.actionItem,
                title: titleOf(view),
                text: view.lastMessage.text,
                byName: view.lastMessage.byName,
                at: Date.now(),
            }
        } else if (state.incoming?.conversationId === view._id) {
            // Edited or deleted before it was read: the pill never shows what is gone.
            // null: no message left. Absent: a view that doesn't say.
            if (view.lastMessage === null) {
                patch.incoming = null
            } else if (view.lastMessage?.text) {
                patch.incoming = {
                    ...state.incoming,
                    text: view.lastMessage.text,
                    byName: view.lastMessage.byName,
                }
            }
        }

        if (view.kind === "task") {
            if (view.actionItem) taskConversations.set(view.actionItem, view._id)

            const index = state.tasks.tasks.findIndex((t) => t._id === view.actionItem)

            if (index === -1) {
                // A task they were just put on or mentioned in: the column reloads.
                if (state.open && state.tasksLoaded) findTask(view.actionItem)
            } else {
                const tasks = state.tasks.tasks.slice()
                tasks[index] = {
                    ...tasks[index],
                    conversation: view._id,
                    unread: view.unread,
                    lastMessage: view.lastMessage,
                    lastMessageAt: view.lastMessageAt,
                    lastSelectedAt: view.lastSelectedAt,
                }
                patch.tasks = { ...state.tasks, tasks }
            }
        } else if (state.conversationsLoaded) {
            const previous = state.conversations.find((c) => c._id === view._id)
            // A frame's members may come without names (a read receipt) or not at all
            // (a group's message): keep the ones we have.
            const keepMembers = !view.members || view.members.some((m) => typeof m.person === "string")
            const members = keepMembers && previous ? previous.members : view.members

            // Never seen, and no members to show it with: read the list again (once for it)
            if (!previous && !view.members && !listReloadedFor.has(view._id)) {
                listReloadedFor.add(view._id)
                loadConversations({ force: true })
            }

            patch.conversations = [
                ...state.conversations.filter((c) => c._id !== view._id),
                { ...previous, ...view, members: members || [] },
            ].sort(byLatest)
        }

        set(patch)

        if (arrived) incomingListeners.forEach((fn) => fn(patch.incoming))
        if (arrived && isOnScreen(view)) readOpenRoomSoon()
    }

    /** Whether a conversation is the room open and in sight. */
    const isOnScreen = (view) => {
        const room = state.room

        return (
            state.open &&
            !state.collapsed &&
            document.visibilityState === "visible" &&
            Boolean(room) &&
            (room.conversationId === view._id || (room.kind === "task" && room.taskId === view.actionItem))
        )
    }

    /** The room on screen got a message: read it a moment later (once for a burst). */
    const readOpenRoomSoon = () => {
        clearTimeout(openRoomReadTimer)
        openRoomReadTimer = setTimeout(() => markRead(state.room), OPEN_ROOM_READ_DELAY_MS)
    }

    /**
     * A frame for a task the column does not list: reload it once, a moment later
     * (a burst of frames is one reload), and remember the ones it still lacks.
     */
    const findTask = (taskId) => {
        if (!taskId) return
        if (Date.now() - (unlistedTasks.get(taskId) || 0) < UNLISTED_TASK_MS) return

        tasksToFind.add(taskId)
        clearTimeout(tasksReloadTimer)
        tasksReloadTimer = setTimeout(() => {
            const wanted = [...tasksToFind]

            tasksToFind.clear()
            loadTasks({ force: true }).then(() => {
                wanted
                    .filter((id) => !state.tasks.tasks.some((t) => t._id === id))
                    .forEach((id) => unlistedTasks.set(id, Date.now()))
            })
        }, TASKS_RELOAD_DELAY_MS)
    }

    /** Called with each message that comes in (the beep). */
    const onIncoming = (fn) => {
        incomingListeners.add(fn)
        return () => incomingListeners.delete(fn)
    }

    /** Opens the chat on the latest message that came in, if it is still unread. */
    const openIncoming = () => {
        const incoming = state.incoming

        if (incoming && state.unread[incoming.conversationId]) {
            if (incoming.kind === "task") selectTask(incoming.actionItem)
            else selectConversation(incoming.conversationId)
        }

        expand()
    }

    const removeConversation = (conversationId) => {
        const unread = { ...state.unread }
        delete unread[conversationId]

        const patch = { unread, conversations: state.conversations.filter((c) => c._id !== conversationId) }

        if (state.room?.conversationId === conversationId) patch.room = null
        if (state.contextId === conversationId) patch.contextId = null
        if (lastRoom()?.conversationId === conversationId) forgetRoom()

        set(patch)
    }

    /** The badge, from the server. */
    const loadUnread = () => {
        const gen = generation

        return client
            .unread()
            .then((res) => {
                if (gen !== generation) return

                const unread = {}
                ;(res?.conversations || []).forEach((row) => {
                    unread[row._id] = row
                })
                set({ unread })
            })
            .catch(() => {})
    }

    /** The connection dropped and came back: frames were missed, read again what is shown. */
    const resync = () => {
        clearTimeout(resyncTimer)
        resyncTimer = setTimeout(() => {
            loadUnread()

            // Shown: read again now. Closed: read again when it next shows
            if (state.open && state.conversationsLoaded) loadConversations({ force: true })
            else if (state.conversationsLoaded) set({ conversationsLoaded: false })
        }, Math.random() * RESYNC_SPREAD_MS)
    }

    /** Signed in: the badge, and the socket from now on. */
    const start = () => {
        loadUnread()

        if (sockets.length) return

        sockets = [
            client.onConversationChanges(
                "update",
                (changes) => {
                    ;(changes || []).forEach((change) => applyView(change.doc))
                },
                resync,
            ),
            client.onConversationChanges("delete", (changes) => {
                ;(changes || []).forEach((change) => removeConversation(change._id))
            }),
        ]
    }

    /** Signed out: forget everything and stop listening. */
    const reset = () => {
        generation += 1
        sockets.forEach((socket) => socket?.disconnect?.())
        sockets = []
        tasksLoading = null
        versions.clear()
        unlistedTasks.clear()
        tasksToFind.clear()
        listReloadedFor.clear()
        taskConversations.clear()
        clearTimeout(tasksReloadTimer)
        clearTimeout(openRoomReadTimer)
        clearTimeout(resyncTimer)
        tasksLoadedAt = 0
        // The next person on this browser never opens on this one's chat
        forgetRoom()
        state = initialState()
        listeners.forEach((fn) => fn(state))
    }

    const loadConversations = ({ force = false } = {}) => {
        if (state.conversationsLoaded && !force) return Promise.resolve()

        const gen = generation

        return client
            .listConversations()
            .then((list) => {
                if (gen !== generation) return

                set({
                    conversations: list.slice().sort(byLatest),
                    conversationsLoaded: true,
                    unread: list.reduce(withUnread, state.unread),
                })
            })
            .catch(() => {
                if (gen === generation) set({ conversationsLoaded: true })
            })
    }

    /** When chat shows again: the task column, unless it was read in the last minute. */
    const refreshTasks = () => loadTasks({ force: !state.tasksLoaded || Date.now() - tasksLoadedAt > TASKS_REFRESH_MS })

    const loadTasks = ({ force = false } = {}) => {
        if (state.tasksLoaded && !force) return Promise.resolve()
        if (tasksLoading) return tasksLoading

        const gen = generation

        tasksLoading = client
            .listTasks()
            .then((res) => {
                if (gen !== generation) return

                tasksLoadedAt = Date.now()
                set({ tasks: { missions: res?.missions || [], tasks: res?.tasks || [] }, tasksLoaded: true })
            })
            .catch(() => {
                if (gen === generation) set({ tasksLoaded: true })
            })
            .finally(() => {
                tasksLoading = null
            })

        return tasksLoading
    }

    const loadPeople = ({ force = false } = {}) => {
        if (state.peopleLoaded && !force) return Promise.resolve()

        const gen = generation

        return client
            .people()
            .then((people) => {
                if (gen === generation) set({ people, peopleLoaded: true })
            })
            .catch(() => {
                if (gen === generation) set({ peopleLoaded: true })
            })
    }

    const selectConversation = (conversationId) => {
        rememberRoom({ kind: "conversation", conversationId })
        set({ room: { kind: "conversation", conversationId }, contextId: conversationId })
    }

    const selectTask = (taskId) => {
        rememberRoom({ kind: "task", taskId })
        set({ room: { kind: "task", taskId } })
    }

    const selectGeneral = () => {
        if (state.contextId) selectConversation(state.contextId)
    }

    const startNew = () => set({ room: { kind: "new" } })

    /**
     * Opens the chat, optionally on a room.
     *
     * @param [options] {Object}
     * @param [options.conversationId] {String}
     * @param [options.taskId] {String}
     * @param [options.personId] {String} Opens (or starts) the direct message with them
     */
    const openChat = async (options = {}) => {
        set({ open: true, collapsed: false })

        if (options.personId) {
            try {
                const view = await client.createConversation([options.personId])

                applyView(view)
                selectConversation(view._id)
            } catch (err) {
                toast.warning(err?.message || "Couldn't open that chat. Try again in a moment.")
            }
        } else if (options.conversationId) {
            selectConversation(options.conversationId)
        } else if (options.taskId) {
            selectTask(options.taskId)
        } else if (!state.room || state.room.kind === "new") {
            // Nowhere asked for: back where they were last (a refresh included)
            const last = lastRoom()

            if (last?.kind === "conversation" && last.conversationId) selectConversation(last.conversationId)
            else if (last?.kind === "task" && last.taskId) selectTask(last.taskId)
        }
    }

    const close = () => set({ open: false, collapsed: false })

    const collapse = () => set({ collapsed: true })

    const expand = () => set({ collapsed: false, open: true })

    const setMode = (mode) => {
        localStorage.setItem(MODE_KEY, mode)
        set({ mode })
    }

    const findConversation = (conversationId) => state.conversations.find((c) => c._id === conversationId)

    /**
     * The open room was read: its count and the badge go to zero at once.
     * `opened`: they opened it. A task room joins its thread then (the heavy
     * call); after that, a task's reads are its conversation's, like any chat.
     */
    const markRead = (room, { opened = false } = {}) => {
        if (!room) return Promise.resolve()

        if (room.kind === "task") {
            const conversationId =
                state.tasks.tasks.find((t) => t._id === room.taskId)?.conversation || taskConversations.get(room.taskId)

            if (!opened && conversationId) {
                // Nothing unread: nothing to ask the server
                if (!state.unread[conversationId]) return Promise.resolve()

                return client
                    .markRead(conversationId)
                    .then(applyView)
                    .catch(() => {})
            }

            return client
                .openTask(room.taskId)
                .then(applyView)
                .catch(() => {})
        }

        if (room.kind === "conversation") {
            const row = state.unread[room.conversationId]

            // Not opening it and nothing unread: nothing to ask the server
            if (!row && !opened) return Promise.resolve()

            if (row) {
                applyView({
                    ...findConversation(room.conversationId),
                    _id: room.conversationId,
                    org: row.org,
                    kind: row.kind,
                    unread: 0,
                })
            }

            return client
                .markRead(room.conversationId, { opened })
                .then(applyView)
                .catch(() => {})
        }

        return Promise.resolve()
    }

    const toggleMission = (missionId) =>
        set({ openMissions: { ...state.openMissions, [missionId]: !state.openMissions[missionId] } })

    const toggleRooms = () => {
        localStorage.setItem(ROOMS_FOLDED_KEY, String(!state.roomsFolded))
        set({ roomsFolded: !state.roomsFolded })
    }

    return {
        getState,
        subscribe,
        totalUnread,
        onIncoming,
        openIncoming,
        start,
        reset,
        loadConversations,
        loadTasks,
        refreshTasks,
        loadPeople,
        openChat,
        close,
        collapse,
        expand,
        setMode,
        selectConversation,
        selectTask,
        selectGeneral,
        startNew,
        markRead,
        findConversation,
        applyView,
        removeConversation,
        toggleMission,
        toggleRooms,
    }
}

const chatStore = createChatStore()

export default chatStore

export { createChatStore }

/**
 * Reads the chat store and re-renders when what the selector picks changes.
 *
 * @param [selector] {function(Object): *}
 * @param [store] {Object} another store (tests)
 * @return {*}
 */
export function useChat(selector = (s) => s, store = chatStore) {
    const [, rerender] = useReducer((n) => n + 1, 0)
    const selected = selector(store.getState())
    const selectorRef = useRef(selector)
    const selectedRef = useRef(selected)

    selectorRef.current = selector
    selectedRef.current = selected

    useEffect(() => {
        const check = (state) => {
            if (!Object.is(selectedRef.current, selectorRef.current(state))) rerender()
        }

        check(store.getState())

        return store.subscribe(check)
    }, [store])

    return selected
}
